import { useMemo } from 'react'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import type { ChecklistItem } from '../../../../types/timeline'
import type { TimelineElement } from '../../../../types/timelineElement'
import type { LiveModuleHeadline, LiveModuleRecord, LiveModuleSparkline } from '../LiveModuleCard'
import { bucketize, calculateDelta, timeRangeBucketCount, timeRangeToDateRange } from './bucketize'

export interface UseContributionActivityModuleParams {
  workspaceId: string
  elements?: TimelineElement[]
  timeRange: AnalysisTimeRangeKey
  resolveMemberLabel?: (uid: string) => string | null
  refreshKey?: number
  now?: Date
}

export interface UseContributionActivityModuleResult {
  headline: LiveModuleHeadline
  sparkline: LiveModuleSparkline
  records: LiveModuleRecord[]
  loading: boolean
  error: Error | null
}

const parseDate = (val: unknown): Date | null => {
  if (!val) return null
  if (val instanceof Date) return val
  if (typeof (val as { toDate?: () => Date }).toDate === 'function') {
    return (val as { toDate: () => Date }).toDate()
  }
  if (typeof val === 'string' || typeof val === 'number') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

const isChecklistCompleted = (item: ChecklistItem): boolean =>
  item.isCompleted || item.workflowStatus === 'COMPLETED' || item.workflowStatus === 'NOT_REQUIRED'

const isItemOverdue = (item: ChecklistItem, now: Date): boolean => {
  const endDateStr = item.scheduleEndDate ?? item.scheduleStartDate
  if (!endDateStr) return false
  const end = new Date(endDateStr)
  return !isNaN(end.getTime()) && end < now
}

export const useContributionActivityModule = ({
  workspaceId,
  elements = [],
  timeRange,
  resolveMemberLabel,
  refreshKey,
  now = new Date(),
}: UseContributionActivityModuleParams): UseContributionActivityModuleResult => {
  return useMemo(() => {
    void refreshKey

    const bucketCount = timeRangeBucketCount(timeRange)

    if (!workspaceId) {
      return {
        headline: { value: '—', label: 'no records' },
        sparkline: { points: Array(bucketCount).fill(0), direction: 'up-is-good' },
        records: [],
        loading: false,
        error: null,
      }
    }

    const { from, to } = timeRangeToDateRange(timeRange, now)
    const workspaceElements = elements.filter((el) => el.workspaceId === workspaceId)

    const allChecklistItems: ChecklistItem[] = []
    for (const el of workspaceElements) {
      if (el.checklist) {
        allChecklistItems.push(...el.checklist)
      }
    }

    // Collect completed items with assignees
    const completedItems = allChecklistItems.filter(isChecklistCompleted)

    // Match dates in window
    const completedDatesInWindow: Date[] = []
    let completedInWindowCount = 0

    for (const item of completedItems) {
      const dateVal = (item as { completedAt?: unknown; lastUpdatedAt?: unknown; assignedAt?: unknown }).completedAt
        ?? (item as { lastUpdatedAt?: unknown }).lastUpdatedAt
        ?? item.assignedAt
      const parsed = parseDate(dateVal)

      if (parsed) {
        if (parsed >= from && parsed <= to) {
          completedDatesInWindow.push(parsed)
          completedInWindowCount += 1
        }
      }
    }

    if (completedInWindowCount === 0) {
      return {
        headline: { value: '—', label: 'no records' },
        sparkline: { points: Array(bucketCount).fill(0), direction: 'up-is-good' },
        records: [],
        loading: false,
        error: null,
      }
    }

    // Sparkline derivation per Rule 5: bucketize if timestamps available, else points: []
    let points: number[]
    let delta: number
    if (completedDatesInWindow.length > 0) {
      points = bucketize(completedDatesInWindow, timeRange, now)
      delta = calculateDelta(points)
    } else {
      points = []
      delta = 0
    }

    // Aggregate contributor stats across all assignees
    const contributorStats = new Map<string, { id: string; completed: number; overdue: number }>()

    for (const item of allChecklistItems) {
      if (!item.assigneeId) continue
      const existing = contributorStats.get(item.assigneeId) ?? {
        id: item.assigneeId,
        completed: 0,
        overdue: 0,
      }

      if (isChecklistCompleted(item)) {
        existing.completed += 1
      } else if (isItemOverdue(item, now)) {
        existing.overdue += 1
      }

      contributorStats.set(item.assigneeId, existing)
    }

    const topRecords: LiveModuleRecord[] = Array.from(contributorStats.values())
      .filter((c) => c.completed > 0 || c.overdue > 0)
      .sort((a, b) => b.completed - a.completed || a.overdue - b.overdue)
      .slice(0, 5)
      .map((contributor) => {
        const name = resolveMemberLabel?.(contributor.id)?.trim()
          || `Assignee ${contributor.id.slice(0, 6)}`
        return {
          id: `contributor-${contributor.id}`,
          primary: name,
          secondary: `${contributor.completed} completed · ${contributor.overdue} overdue`,
          numeric: contributor.completed,
          drillTarget: 'CHECKLISTS',
        }
      })

    const headline: LiveModuleHeadline = {
      value: completedInWindowCount,
      delta,
      label: 'completed',
    }

    return {
      headline,
      sparkline: { points, direction: 'up-is-good' },
      records: topRecords,
      loading: false,
      error: null,
    }
  }, [workspaceId, elements, timeRange, resolveMemberLabel, refreshKey, now])
}
